import logger from 'utils/logger';

const normalizeString = (str: string = '') => str.trim().toLowerCase();

export default normalizeString;

// below withNetworkRetries could be adapted to do retries for any promise using library in the app if needed

// 2 calls in < 250ms seem to internally get debounced by cognito so go over 250
const RETRY_AT_MS = [250, 500, 1000, 2000];

/**
 * do retries on cognito failures
 * cognito doesn't attempt to retry on network errors - network errors are a given in a mobile app
 * and are the source of lots of bad reviews
 * @param func the func to run
 * @param funcName  name for logging
 * @returns the result of the promise
 */
export const withNetworkRetries = <TResult>(func: () => Promise<TResult>, funcName: string) => {
  let retryAttemptIndex = 0;

  const runFunc = () =>
    func().catch((err: AuthError) => {
      if (err.code === 'NetworkError') {
        if (RETRY_AT_MS[retryAttemptIndex]) {
          logger.error({
            message: `[CognitoNetworkError] ${funcName} failed, retrying ${retryAttemptIndex + 1}/${
              RETRY_AT_MS.length
            }`,
            isCognitoNetworkError: true,
          });
          return new Promise((res, rej) => {
            setTimeout(() => {
              retryAttemptIndex++;
              runFunc().then(res).catch(rej);
            }, RETRY_AT_MS[retryAttemptIndex]);
          });
        }

        // exhausted retries
        logger.error({
          message: `[CognitoNetworkError] ${funcName} failed. No more retries ${RETRY_AT_MS.length}/${RETRY_AT_MS.length}`,
          isCognitoNetworkError: true,
        });
      }
      // let all other errors pass though normally
      return Promise.reject(err);
    });

  return runFunc() as Promise<TResult>;
};

interface AuthError extends Error {
  code: string | 'NetworkError';
}
